import {StorageUtility} from './Storage';
import {Locale} from './Locale';
import {Helpers} from './Helpers';

declare var window: any;
declare var $: any;

export class URLManager {
    private static instance: URLManager;

    static getInstance(): URLManager {
        if (!URLManager.instance) {
            URLManager.instance = new URLManager();
        }
        return URLManager.instance;
    }

    public getParams() {
        const returnObject = {};
        if (location.search) {
            const parts = location.search.substring(1).split('&');
            parts.forEach(function (part) {
                const pair = part.split('=');
                if (pair[0]) {
                    if (pair[1]) {
                        // Sanitize parameter values
                        const sanitizedValue = URLManager.getInstance().sanitizeUrlParameter(
                            pair[1]
                        );
                        if (sanitizedValue !== null) {
                            returnObject[pair[0]] = sanitizedValue;
                        }
                    } else {
                        returnObject[pair[0]] = true;
                    }
                }
            });
        }
        return returnObject;
    }

    /**
     * @summary Sanitizes URL parameter values to prevent XSS and malicious content.
     * @since 17.6.0
     * @param {string} value - The URL parameter value to sanitize.
     * @returns {string|null} Sanitized value or null if blocked.
     */
    private sanitizeUrlParameter(value: string): string | null {
        if (!value) return value;

        try {
            // Decode URL parameter
            const decoded = decodeURIComponent(value);

            // Block dangerous patterns
            const dangerousPatterns = [
                /javascript:/i,
                /data:/i,
                /vbscript:/i,
                /onload=/i,
                /onerror=/i,
                /onclick=/i,
                /onmouseover=/i,
                /\x00/, // null bytes
                /<script/i,
                /<iframe/i,
                /<object/i,
                /<embed/i,
                /expression\(/i,
                /url\(/i,
            ];

            for (const pattern of dangerousPatterns) {
                if (pattern.test(decoded)) {
                    console.warn(
                        '[Security] Blocked suspicious URL parameter:',
                        Helpers.sanitizeForLogging(value)
                    );
                    return null;
                }
            }

            // Additional check for encoded dangerous content
            const lowercaseDecoded = decoded.toLowerCase();
            if (
                lowercaseDecoded.includes('javascript:') ||
                lowercaseDecoded.includes('data:') ||
                lowercaseDecoded.includes('vbscript:')
            ) {
                console.warn(
                    '[Security] Blocked suspicious URL parameter:',
                    Helpers.sanitizeForLogging(value)
                );
                return null;
            }

            return value;
        } catch (e) {
            // If decoding fails, treat as suspicious
            console.warn(
                '[Security] Failed to decode URL parameter, blocking:',
                Helpers.sanitizeForLogging(value)
            );
            return null;
        }
    }

    public getDefaultParams() {
        const parameters = {
            locale: Locale.getInstance().getCurrentLocale(),
        };

        if (StorageUtility.getInstance().getLocal('lead_source')) {
            parameters['leadSource'] = StorageUtility.getInstance().getLocal('lead_source');
        }

        if (StorageUtility.getInstance().getLocal('lead_source_params')) {
            parameters['leadSourceUrl'] = StorageUtility.getInstance().getLocal(
                'lead_source_params'
            );
        }

        return parameters;
    }

    public removeParam(key) {
        let url = document.location.href;
        let params: any = url.split('?');
        if (params.length === 1) return;

        url = params[0] + '?';
        params = params[1];
        params = params.split('&');

        $.each(params, (index, value) => {
            const v = value.split('=');
            if (v[0] !== key) url += value + '&';
        });

        url = url.replace(/&$/, '');
        url = url.replace(/\?$/, '');

        return url;
    }

    public appendParameter(url, key, value) {
        if (url.indexOf('?') !== -1) {
            url = url + '&' + key + '=' + value;
        } else url = url + '?' + key + '=' + value;
        return url;
    }

    /**
     * @summary Returns a unique page key for SalesForce tracking.
     * @since 12.0.0
     * @returns {string} Page key to be used with form submission.
     */
    public getPageKey() {
        return location.pathname + location.search + location.hash;
    }

    /**
     * @summary Changes URL for dynamic queries.
     * @since 12.0.0
     * @param {string} path - The new URL path to replace the existing.
     */
    public changePathInURL = (path, keepInHistory = false) => {
        let currentLocale = Locale.getInstance().getCurrentLocale();
        let base = window.location.origin;

        // Add trailing slash to base
        if (base[base.length - 1] !== '/') {
            base = base + '/';
        }

        // Remove initial slash in path since base has one
        if (path[0] === '/') {
            path = path.split('');
            path = path.splice(1, path.length - 1);
            path = path.join('');
        }

        // Handle Locale in URL
        if (currentLocale !== 'en_US') {
            if (path.indexOf(currentLocale) === -1) {
                path = currentLocale + '/' + path;
            }
        }

        // Whether or not to keep in browser history
        if (keepInHistory) {
            history.pushState(null, '', base + path);
        } else {
            history.replaceState(null, '', base + path);
        }
    };

    /**
     * @summary Replaces URI with new one.
     * @since 14.2.0
     * @param {string} path - The new URL path to replace the existing.
     */
    public replaceURI = (newURI, keepInHistory = false) => {
        if (!newURI) {
            return;
        }

        // Whether or not to keep in browser history
        if (keepInHistory) {
            history.pushState(null, '', newURI);
        } else {
            history.replaceState(null, '', newURI);
        }
    };

    /**
     * @summary Returns the provided (or current) path, removing any locale slug (Example: '/threats/malware/')
     * @since 16.18.14
     * @param {string} pathname - The key to append.
     */
    public getPathWithoutLocale(pathname = document.location.pathname) {
        return pathname.replace(/^(\/[a-z]{2}_[A-Z]{2})?/, '');
    }

    /**
     * @summary Returns the canonical path, including any locale slug (Example: '/ja_JP/yayoi-deal-registration/')
     * @since 16.18.14
     */
    public getCanonicalPath() {
        try {
            const canonical: HTMLAnchorElement = document.querySelector('link[rel=canonical]');
            return new URL(canonical.href).pathname;
        } catch (e) {
            console.warn(e);
        }
    }
}

export default URLManager;
